import React, {Component} from 'react';
import PropTypes from 'prop-types';
import DefaultErrorComponent from '../DefaultErrorComponent/DefaultErrorComponent';

class ErrorBoundary extends Component {
    static getDerivedStateFromError() {
        return {hasError: true};
    }

    static propTypes = {
        /** When set to true, the error boundary is bypassed and the error object is passed further up. Useful when you have/want to handle your own errors. */
        shouldThrowOnError: PropTypes.bool,
        /** Any function that does server or client logging for the cosuming application should be passed here,
         * and is executed during the `componentDidCatch` lifecycle method.
         */
        logError: PropTypes.func,
        /** Object strings that will be passed to the default error message component. */
        messages: PropTypes.shape({
            title: PropTypes.string,
            msg: PropTypes.string,
            'btn.label': PropTypes.string,
        }),
        /** Options for handling reloading attempts. */
        reloadOptions: PropTypes.shape({
            /** A function that returns a Promise representing whether the reload was successful */
            onReload: PropTypes.func,
            /** Sets the button to a unclickable state with loading indicator when true. */
            loading: PropTypes.bool,
        }),
        /** You can pass in your own custom functional component to be rendered.
         * The custom component will be passed the messages, reload options, and <ErrorBoundary>'s handleReload() function as arguments if you'd like to utilize them in your custom component. */
        customComponent: PropTypes.func,
    };

    static defaultProps = {
        shouldThrowOnError: false,
        reloadOptions: {
            loading: false,
        },
    };

    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    componentDidCatch = (error) => {
        if (this.props.logError) {
            this.props.logError(error);
        }
        if (this.props.shouldThrowOnError) {
            throw error;
        }
    };

    handleReload = () => {
        /* istanbul ignore else */
        if (this.props.reloadOptions.onReload) {
            this.props.reloadOptions.onReload().then(() => {
                this.setState({hasError: false});
            });
        }
    };

    render() {
        const {customComponent, messages, reloadOptions, children} = this.props;
        if (this.state.hasError) {
            if (customComponent) {
                return customComponent(
                    messages,
                    this.handleReload,
                    reloadOptions
                );
            }
            return (
                <DefaultErrorComponent
                    messages={messages}
                    reload={this.handleReload}
                    reloadOptions={reloadOptions}
                />
            );
        }
        return children;
    }
}

export default ErrorBoundary;
